import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import TimeSlot from "./time_slot";
import { createWorkingHoursCompound } from "../services/api_working_hours";
import { validateTimeSlots } from "../utils/timeslots";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import { EyeSlashIcon } from "@heroicons/react/24/outline";
import { normalizeBreaks, toMin } from "../utils/useWeeklyShiftEditor";
import { getUiErrorMessage } from "../services/utils";

// helper: ordina e normalizza payload
function toPayloadTimeSlots(slots) {
  return (slots || [])
    .slice()
    .sort((a, b) => (a.workStartTime || "").localeCompare(b.workStartTime || ""))
    .map((s) => ({
      start_at: s.workStartTime,
      end_at: s.workEndTime,
      breaks: normalizeBreaks(s.breaks || []).map((br) => ({
        start_at: br.start_at,
        end_at: br.end_at,
        is_visible: br.is_visible ?? true,
      })),
    }));
}

function minutesToHHMM(totalMinutes) {
  const m = Math.max(0, Math.round(totalMinutes || 0));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function computeDailyTotalMinutes(slots) {
  return (slots || []).reduce((sum, s) => {
    const ws = toMin(s?.workStartTime);
    const we = toMin(s?.workEndTime);
    if (ws == null || we == null) return sum;

    // bloccare cross-midnight nel daily create:
    if (we < ws) return sum;

    const workMinutes = Math.max(0, we - ws);

    const breakMinutes = normalizeBreaks(s?.breaks || []).reduce((bSum, br) => {
      const bs = toMin(br?.start_at);
      const be = toMin(br?.end_at);
      if (bs == null || be == null) return bSum;
      return bSum + Math.max(0, be - bs);
    }, 0);

    return sum + Math.max(0, workMinutes - breakMinutes);
  }, 0);
}

export default function CreateDailyShiftForm({
  token,
  selectedOu = null,          // OU già scelta nel parent
  allowHiddenBreaks = false,  // se vuoi passarlo dal parent
  onSuccess,                  // callback
}) {
  const [name, setName] = useState("");
  const [slots, setSlots] = useState([]);

  // editor fascia (se non null -> stai editando/creando)
  const [editingIndex, setEditingIndex] = useState(null);
  const editingSlot = useMemo(() => {
    if (editingIndex === null) return null;
    return slots[editingIndex] || null;
  }, [editingIndex, slots]);

  const totalWorkingTime = useMemo(() => {
    const totalMin = computeDailyTotalMinutes(slots);
    return minutesToHHMM(totalMin);
  }, [slots]);


  const openNewSlot = () => {
    setEditingIndex(slots.length); // nuova fascia in fondo
  };

  const openEditSlot = (idx) => setEditingIndex(idx);

  const handleCancelEdit = () => setEditingIndex(null);

  const handleSaveSlot = (timeSlotData) => {
    try {
      const ws = toMin(timeSlotData.workStartTime);
      const we = toMin(timeSlotData.workEndTime);
      if (ws == null || we == null) throw new Error("Orari non validi.");
      if (we < ws) throw new Error("Nel turno giornaliero non è consentito attraversare la mezzanotte.");

      const newSlot = { start_at: timeSlotData.workStartTime, end_at: timeSlotData.workEndTime };

      const existing = slots
        .filter((_, i) => i !== editingIndex)
        .map((s) => ({ start_at: s.workStartTime, end_at: s.workEndTime }));

      validateTimeSlots(newSlot.start_at, newSlot.end_at, existing);

      setSlots((prev) => {
        if (editingIndex === null) return prev;
        const next = [...prev];
        next[editingIndex] = { ...timeSlotData, breaks: normalizeBreaks(timeSlotData.breaks || []) };
        return next;
      });

      setEditingIndex(null);
    } catch (e) {
      Swal.fire({
        icon: "error",
        title: "Fascia non valida",
        text: e?.message || "La fascia si sovrappone ad una già esistente.",
      });
    }
  };

  const removeSlot = (idx) => {
    setSlots((prev) => prev.filter((_, i) => i !== idx));
    if (editingIndex === idx) setEditingIndex(null);
  };

  const handleCreate = async () => {
    if (!name.trim()) {
      return Swal.fire({
        icon: "warning",
        title: "Nome obbligatorio",
        text: "Inserisci il nome del turno.",
      });
    }

    if (!slots.length) {
      return Swal.fire({
        icon: "warning",
        title: "Aggiungi una fascia",
        text: "Inserisci almeno una fascia oraria.",
      });
    }

    const payload = {
      name: name.trim(),
      ou: selectedOu?.id ?? undefined,
      time_slots: toPayloadTimeSlots(slots),
    };
    if (!selectedOu?.id) delete payload.ou;

    try {
      Swal.fire({
        title: "Creazione turno...",
        icon: "info",
        showConfirmButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => Swal.showLoading(),
      });

      await createWorkingHoursCompound(token, payload);

      Swal.close();
      await Swal.fire({
        icon: "success",
        title: "Turno creato",
        text: "Il turno giornaliero è stato creato correttamente.",
        confirmButtonText: "OK",
      });

      // reset locale
      setName("");
      setSlots([]);
      setEditingIndex(null);

      onSuccess?.();
    } catch (error) {
      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: getUiErrorMessage(error),
      });
    }
  };

  return (
    <div className="mt-4 rounded-b-lg border border-gray-100 bg-gray-100/60 py-4 px-5 space-y-4">
      {/* Lista fasce */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold uppercase text-indigo-800">
              Fasce orarie
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Aggiungi una o più fasce. Puoi inserire anche le pause.
            </div>
          </div>

          <button
            type="button"
            onClick={openNewSlot}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            + AGGIUNGI FASCIA
          </button>
        </div>

        {/* elenco compatto */}
        {slots.length === 0 ? (
          <div className="mt-4 text-sm font-normal uppercase text-gray-500">{editingIndex === null ? ('Nessun orario inserito') : ('')}</div>
        ) : (
          <div className="mt-4 space-y-2">
            {slots.map((s, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-800">
                    {s.workStartTime} - {s.workEndTime}
                    {s.crossesMidnight ? <span className="text-xs text-indigo-700 ml-2">(notturno)</span> : null}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 border border-indigo-100">
                      <LocalBarIcon style={{ height: 12, width: 12, color: "#4f46e5" }} />
                      Pause
                    </span>

                    {Array.isArray(s.breaks) && s.breaks.length > 0 ? (
                      s.breaks.map((br, j) => {
                        const hidden = br?.is_visible === false;
                        // key: se non hai id lato client usa indice+orari
                        const key = br?.id ?? `${j}-${br.start_at}-${br.end_at}`;

                        return (
                          <span
                            key={key}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold border ${
                              hidden
                                ? "bg-gray-100 text-gray-500 border-gray-200"
                                : "bg-white text-gray-700 border-gray-200"
                            }`}
                            title={hidden ? "Pausa nascosta" : "Pausa visibile"}
                          >
                            {hidden && <EyeSlashIcon className="h-3.5 w-3.5 text-gray-500" />}
                            {br.start_at} - {br.end_at}
                          </span>
                        );
                      })
                    ) : (
                      <span className="text-[11px] text-gray-400 ml-1">Nessuna</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEditSlot(idx)}
                    className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                  >
                    Modifica
                  </button>
                  <button
                    type="button"
                    onClick={() => removeSlot(idx)}
                    className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                  >
                    Rimuovi
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Editor fascia */}
        {editingIndex !== null && (
          <div className="mt-4">
            <TimeSlot
              shiftPreview={editingSlot || { workStartTime: "", workEndTime: "", breaks: [] }}
              allowHiddenBreaks={allowHiddenBreaks}
              onShiftPreview={handleSaveSlot}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={() => {}}
            />
          </div>
        )}
        {totalWorkingTime !== "00:00" && (
          <div className="mt-4 -mx-4 -mb-4 rounded-b-xl">
            <div className="flex items-center justify-between gap-3 bg-gray-50 px-4 py-2 border-t border-gray-200 rounded-b-xl ">
              <div className="text-xs font-semibold uppercase text-gray-700">
                Totale ore giornaliere
              </div>
              <div className="inline-flex items-center rounded-full bg-white px-2.5 py-1 text-[13px] font-semibold text-gray-800 border border-gray-200 uppercase">
                <span className="text-indigo-700">{totalWorkingTime}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Nome turno */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
        <div className="text-sm font-semibold uppercase text-indigo-800">
          Nome del turno giornaliero
        </div>
        <div className="text-xs text-gray-500 mt-1">
          * Inserisci il nome
        </div>

        <div className="mt-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Es. Mattina - Magazzino / Turno 7:30"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      </div>
      
      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleCreate}
          className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          Crea turno giornaliero
        </button>
      </div>
    </div>
  );
}